/* eslint-disable no-restricted-syntax */
/* eslint-disable no-await-in-loop */
import { Component } from 'react';
import { connect } from 'react-redux';
import autoBind from 'react-autobind';
import { bindActionCreators } from 'redux';
import classNames from 'classnames/bind';
import loadable from '@utils/loadable';

import Alert from '../../Shared/ReactAlert';
import WatchlistPhaseLoader from '../WatchlistPhaseLoader';

import permissionsDecorator from '../../../decorators/permissionsDecorator';
import withComponentName from '../../../decorators/withComponentName';

import StorageSvc from '../../../services/StorageSvc';

import * as searchActions from '../../../actions/searchActions';
import * as watchlistActions from '../../../actions/watchlistActions';
import * as storyActions from '../../../actions/stories.actions';
import * as preferencesActions from '../../../actions/preferencesActions';
import * as commonActions from '../../../actions/common.actions';
import * as trendingActions from '../../../actions/trendingBarActions';

import {
  checkedDuplicateWatchlistName,
  compareAndGetWatchlistsWithoutNameConflicts,
  formatTopicsQuery,
  getSharedIdentifierFromUrl,
} from '../../../helpers/watchlistHelperFunctions';
import { getGuestUserWatchlists } from '../../../helpers/guestUsersWatchlists';
import { SILVER } from '../../../data/permissions';

import Styles from './styles.module.scss';

const WatchlistBody = loadable(() => import('./WatchlistBody'));
const WatchlistStubPage = loadable(() => import('../WatchlistStubPage'));
const SavedChangesMessageModal = loadable(() => import('../../Onboarding/OnboardingStepNav/SavedChangesMessageModal'));
const GuestShareWatchlistStub = loadable(() => import('./GuestShareWatchlistStub'));
const TrendingBarWrapper = loadable(() => import('../../TrendingBar/TrendingBarWrapper'));

const cx = classNames.bind(Styles);

export class WatchlistPageWrapper extends Component {
  constructor(props) {
    super(props);
    autoBind(this);

    this.state = {
      msgModalIsOpen: false,
      isTrendingBarHidden: false,
    };

    this.permissionsInitRef = null;
  }

  componentDidMount() {
    const {
      token, actions, history, permissionsInitialized,
    } = this.props;

    actions.loadPreferences(token, false);

    window.scrollTo(0, 0);

    const sharedIdentifier = getSharedIdentifierFromUrl(history.location.hash);
    if (sharedIdentifier) {
      if (token) {
        actions.activatePreloader();
        actions.createSharedIndentifierWatchlist(sharedIdentifier);
      } else {
        const guestWatchlists = getGuestUserWatchlists();
        if (!guestWatchlists.length) {
          actions.activateSharedWatchlistStub();
        } else {
          actions.activatePreloader();
        }
        actions.addSharedWatchlistToGuest(sharedIdentifier);
      }
    }

    if (permissionsInitialized) {
      this.callWLAPI();
    }
  }

  componentDidUpdate() {
    const { permissionsInitialized } = this.props;

    if (permissionsInitialized && this.permissionsInitRef === null) {
      this.callWLAPI();
    }
  }

  componentWillUnmount() {
    const { actions } = this.props;
    actions.resetWLReduxData();
  }

  handleTrendingBarStatue(status) {
    this.setState({
      isTrendingBarHidden: status,
    });
  }

  onMsgModalClose() {
    this.setState({ msgModalIsOpen: false });
  }

  callWLAPI() {
    const {
      token, actions,
    } = this.props;
    const guestWatchlists = getGuestUserWatchlists();

    if (token) {
      if (StorageSvc.getItem('user_changes_saved') === 'true') {
        setTimeout(() => {
          this.setState({
            msgModalIsOpen: true,
          });
          StorageSvc.removeItem('user_changes_saved');
          setTimeout(() => {
            this.setState({
              msgModalIsOpen: false,
            });
          }, 4000);
        }, 1000);
      }

      if (guestWatchlists.length) {
        actions.guestUserReplacementBegin();
        this.createWatchlistFromGuest(guestWatchlists).then(() => {
          actions.getWatchlists({ onlyCurrentWatchlist: true });
        });
      } else {
        actions.activatePreloader();
        actions.getWatchlists({ onlyCurrentWatchlist: true });
      }
    } else {
      actions.fetchStoriesBegin();
      actions.guestUserWatchlists();
    }
    this.permissionsInitRef = true;
  }

  // create wl for guest user
  async createWatchlistFromGuest(guestWatchlists) {
    const {
      actions, token, isPremium,
    } = this.props;
    actions.activateMultipleRequestsPreloader();
    const userWatchlists = await actions.getUserWatchlists();

    if (!Array.isArray(userWatchlists)) return;

    const watchlistsWithoutDuplicate = compareAndGetWatchlistsWithoutNameConflicts(
      userWatchlists,
      guestWatchlists,
    );

    const checkedWatchlists = [...watchlistsWithoutDuplicate].splice(0, 3 - userWatchlists.length);

    try {
      if (watchlistsWithoutDuplicate?.length > 0 && isPremium) {
        const userWatchlistsUpdated = [...userWatchlists];
        actions.fetchStoriesBegin();

        for (const watchlist of watchlistsWithoutDuplicate) {
          const query = formatTopicsQuery(watchlist.search_queries);

          await actions.createNewWatchlist(
            query, token,
            checkedDuplicateWatchlistName(watchlist.name, userWatchlistsUpdated), false,
          );
        }
      } else if (
        (watchlistsWithoutDuplicate?.length > 0) && !isPremium
        && checkedWatchlists?.length
      ) {
        const userWatchlistsUpdated = [...userWatchlists];
        actions.fetchStoriesBegin();

        for (const watchlist of checkedWatchlists) {
          const query = formatTopicsQuery(watchlist.search_queries);

          await actions.createNewWatchlist(
            query, token,
            checkedDuplicateWatchlistName(watchlist.name, userWatchlistsUpdated), false,
          );
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      StorageSvc.removeItem('watchlist');
      StorageSvc.removeItem('userPreferences');
      actions.disactivateMultipleRequestsPreloader();
      actions.guestUserReplacementSuccess();
    }
  }

  renderComponent() {
    const {
      showStub,
      sharedWatchlistStubPage,
      user,
      piwikEnabled,
      page,
      multipleRequestsPreloader,
    } = this.props;

    if (multipleRequestsPreloader) return null;
    if (!showStub && !sharedWatchlistStubPage) {
      return (
        <WatchlistBody
          user={user}
          piwikEnabled={piwikEnabled}
          page={page}
          handleTrendingBarStatue={this.handleTrendingBarStatue}
        />
      );
    }
    if (showStub && !sharedWatchlistStubPage) {
      return <WatchlistStubPage />;
    }

    return <GuestShareWatchlistStub />;
  }

  render() {
    const {
      searchBarActive,
      blurSearchbar,
      user,
      piwikEnabled,
      showStub,
      sharedWatchlistStubPage,
    } = this.props;

    const {
      msgModalIsOpen, isTrendingBarHidden,
    } = this.state;

    return (
      <div className={cx('watchlist_page_wrapper', { homepage_blured: searchBarActive && blurSearchbar })}>
        <WatchlistPhaseLoader className={Styles['watchlist-phase-loader']} />
        {Boolean(searchBarActive && blurSearchbar) && (
          <div className={Styles.blur_div_background} />
        )}
        <SavedChangesMessageModal
          onMsgModalClose={this.onMsgModalClose}
          msgModalIsOpen={msgModalIsOpen}
          activePage="watchlists"
        />
        {!showStub && !sharedWatchlistStubPage && !isTrendingBarHidden && (
          <TrendingBarWrapper user={user} piwikEnabled={piwikEnabled} />
        )}
        {this.renderComponent()}
        <Alert />
      </div>
    );
  }
}

const mapStateToProps = ({
  watchlistReducer,
  searchReducers,
  subscriptions,
}) => ({
  token: watchlistReducer.userToken,
  searchBarActive: searchReducers.searchBarActive,
  blurSearchbar: searchReducers.blur,
  showStub: watchlistReducer.showWatchlistStub,
  sharedWatchlistStubPage: watchlistReducer.sharedWatchlistStubPage,
  activateFilingsPreloader: watchlistReducer.activateFilingsPreloader,
  isPremium: subscriptions.permissions.access_levels.includes(SILVER),
  permissionsInitialized: subscriptions.permissionsInitialized,
  multipleRequestsPreloader: watchlistReducer.multipleRequestsPreloader,
  loading: watchlistReducer.loading,
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators({
    ...watchlistActions,
    ...searchActions,
    ...storyActions,
    ...preferencesActions,
    ...commonActions,
    ...trendingActions,
  }, dispatch),
});

export default withComponentName(connect(
  mapStateToProps,
  mapDispatchToProps,
)(permissionsDecorator(WatchlistPageWrapper)));
